import { EditorSelection, StateEffect } from "@codemirror/state";
import { EditorView, ViewPlugin } from "@codemirror/view";

import { sourceOnly, toggleVisualEffect } from "./visual.js";

const restoreScrollPositionEffect = StateEffect.define();

function buildStorageKey(docId) {
    return `manuscript.visual.scroll.${docId}`;
}

function readStoredScrollLine(docId) {
    if (!docId || typeof window === "undefined") {
        return 1;
    }

    try {
        const raw = window.localStorage.getItem(buildStorageKey(docId));
        const parsed = raw ? JSON.parse(raw) : null;
        return Math.max(1, Number(parsed?.firstVisibleLine) || 1);
    } catch (error) {
        return 1;
    }
}

function writeStoredScrollLine(docId, firstVisibleLine) {
    if (!docId || typeof window === "undefined") {
        return;
    }

    try {
        window.localStorage.setItem(buildStorageKey(docId), JSON.stringify({ firstVisibleLine }));
    } catch (error) {
        // Ignore storage errors.
    }
}

function calculateFirstVisibleLine(view) {
    const { top } = view.scrollDOM.getBoundingClientRect();
    const distanceFromDocumentTop = top - view.documentTop;
    const firstBlock = view.lineBlockAtHeight(distanceFromDocumentTop);
    const position = Math.min(firstBlock.from, view.state.doc.length);
    return view.state.doc.lineAt(position).number;
}

function scrollStoredLineToTop(state, docId) {
    const firstVisibleLine = Math.min(readStoredScrollLine(docId), state.doc.lines);
    const line = state.doc.line(firstVisibleLine);
    const selectionRange = EditorSelection.cursor(line.from);

    return {
        effects: EditorView.scrollIntoView(selectionRange, {
            y: "start",
            yMargin: 0,
        }),
    };
}

export function restoreScrollPosition() {
    return {
        effects: restoreScrollPositionEffect.of(null),
    };
}

export function scrollPosition(docId, { visual }) {
    let latestFirstVisibleLine = 1;
    let scrollTimer = null;

    return [
        ViewPlugin.define(
            (view) => {
                const unloadListener = () => {
                    writeStoredScrollLine(docId, latestFirstVisibleLine);
                };

                window.addEventListener("unload", unloadListener);

                return {
                    update(update) {
                        for (const transaction of update.transactions) {
                            for (const effect of transaction.effects) {
                                if (effect.is(toggleVisualEffect)) {
                                    writeStoredScrollLine(docId, latestFirstVisibleLine);
                                } else if (effect.is(restoreScrollPositionEffect)) {
                                    window.setTimeout(() => {
                                        view.dispatch(scrollStoredLineToTop(view.state, docId));
                                        window.dispatchEvent(new Event("editor:scroll-position-restored"));
                                    });
                                }
                            }
                        }
                    },
                    destroy() {
                        if (scrollTimer) {
                            window.clearTimeout(scrollTimer);
                        }
                        window.removeEventListener("unload", unloadListener);
                        unloadListener();
                    },
                };
            },
            {
                eventHandlers: {
                    scroll(event, view) {
                        if (event.target !== view.scrollDOM) {
                            return;
                        }

                        if (scrollTimer) {
                            window.clearTimeout(scrollTimer);
                        }

                        scrollTimer = window.setTimeout(() => {
                            latestFirstVisibleLine = calculateFirstVisibleLine(view);
                            window.dispatchEvent(
                                new CustomEvent("scroll:editor:update", {
                                    detail: latestFirstVisibleLine,
                                }),
                            );
                        }, 120);
                    },
                },
            },
        ),
        sourceOnly(
            visual,
            EditorView.updateListener.of((update) => {
                for (const transaction of update.transactions) {
                    for (const effect of transaction.effects) {
                        if (effect.is(toggleVisualEffect) && !effect.value) {
                            window.setTimeout(() => {
                                update.view.dispatch(restoreScrollPosition());
                                update.view.focus();
                            });
                        }
                    }
                }
            }),
        ),
    ];
}
